import { Box, Container, Grid, Stack, Typography } from "@mui/material";
import { useContext, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import useSWR from "swr";

import Loader from "../../components/Loader";
import PageHeader from "../../components/PageHeader";

import RetailerContext from "../../context";

import { getTranslation } from "../../utils/translate";
import { getBeefProductOverride } from '../../utils/overrides';
import { fetcher } from "../../utils/fetcher";
import { extractOrderedCmsVideoIdsFromLayoutVideos } from "../../utils/recipeVideos";

import { useUpdateDocumentMeta } from "../../hooks/useUpdateDocumentMeta";

import AccordionSection from "../../components/AccordionSection";
import Collection from "../../components/Collection";
import BeefCuts from "../../components/Collection/BeefCuts";
import { useTranslation } from "react-i18next";
import VideoGallery from "../../components/VideoGallery";
import TileCard from "../../components/TileCard";
import Wysiwyg from "../../components/Wysiwyg";
import TileGrid from "../../components/shared/TileGrid";
import Search from "../../components/Search";
import ModelViewer3D from "../../components/ModelViewer3D";
import GordonCard from "../../components/GordonCard";

function BeefPrimal() {
  const language = useSelector(state => state.language);
  const { retailer } = useContext(RetailerContext);
  const { t } = useTranslation();
  const { beefProductId } = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);

  const fields = [
    'Alternate_Food_Service_Image',
    'additional_images.directus_files_id.id',
    'additional_images.directus_files_id.type',
    'beef_product_brandings.beef_product_branding_id.brand_color',
    'beef_product_brandings.beef_product_branding_id.id',
    'beef_product_brandings.beef_product_branding_id.image',
    'beef_product_brandings.beef_product_branding_id.logo',
    'beef_product_brandings.beef_product_branding_id.translations.description',
    'beef_product_brandings.beef_product_branding_id.translations.id',
    'beef_product_brandings.beef_product_branding_id.translations.languages_code',
    'beef_product_brandings.beef_product_branding_id.translations.name',
    'beef_product_brandings.beef_product_branding_id.retailers.retailer_id',
    'beef_product_brandings.beef_product_branding_id.retailer',
    'beef_product_brandings.beef_product_branding_id.status',
    'beef_product_overrides.destination',
    'beef_product_overrides.retailer',
    'beef_product_overrides.status',
    'beef_product_overrides.translations.description',
    'beef_product_overrides.translations.languages_code',
    'beef_product_overrides.translations.name',
    'catalog_code',
    'collections.*.*',
    'children.beef_product_overrides.*',
    'children.beef_product_overrides.translations.*',
    'children.*',
    'children.recipes.recipe_id.retailers',
    'children.translations.*',
    'children.food_service_configurations.*',
    'cooking_methods.id',
    'cooking_methods.cooking_method.translations.id',
    'cooking_methods.cooking_method.translations.languages_code',
    'cooking_methods.cooking_method.translations.Method',
    'cooking_methods.Recommended',
    'cooking_methods.translations.id',
    'cooking_methods.translations.languages_code',
    'cooking_methods.translations.Directions',
    'food_service_configurations.*',
    'food_service_configurations.prep_vids.*',
    'food_service_configurations.prep_vids.videos_id.*',
    'food_service_configurations.merchandising_vids.*',
    'food_service_configurations.merchandising_vids.videos_id.*',
    'food_service_configurations.culinary_vids.*',
    'food_service_configurations.culinary_vids.videos_id.*',
    'id',
    'image',
    'merchandising_sections.*',
    'merchandising_sections.item.id',
    'merchandising_sections.item.status',
    'merchandising_sections.item.translations.*',
    'merchandising_sections.item.videos.id',
    'merchandising_sections.item.videos.status',
    'merchandising_sections.item.videos.translations.*',
    'key_information_blocks.*',
    'key_information_blocks.item.id',
    'key_information_blocks.item.status',
    'key_information_blocks.item.translations.*',
    'key_information_blocks.item.videos.id',
    'key_information_blocks.item.videos.status',
    'key_information_blocks.item.videos.translations.*',
    'Nutrients.calories',
    'Nutrients.id',
    'Nutrients.notes',
    'Nutrients.translations.id',
    'Nutrients.translations.description',
    'Nutrients.translations.languages_code',
    'Nutrients.translations.macronutrients',
    'Nutrients.translations.micronutrients',
    'Nutrients.translations.portion_size',
    'primal_configurations.*',
    'primal_configurations.visual_guide_images.directus_files_id.id',
    'primal_configurations.visual_guide_images.directus_files_id.type',
    'food_service_related_cuts.*.*',
    'food_service_related_cuts.*.translations.*',
    'food_service_related_cuts.*.food_service_configurations.*',
    'threed_file',
    'threed_file_annotated',
    'svg_icon',
    'translations.id',
    'translations.description',
    'translations.languages_id',
    'translations.name',
    'translations.specifications',
    'translations.tips',
    'visual_guide_blocks.*',
    'visual_guide_blocks.item.id',
    'visual_guide_blocks.item.images.id',
    'visual_guide_blocks.item.images.status',
    'visual_guide_blocks.item.images.translations.*',
    'visual_guide_blocks.item.status',
    'visual_guide_blocks.item.translations.*',
    'visual_guide_blocks.item.videos.id',
    'visual_guide_blocks.item.videos.status',
    'visual_guide_blocks.item.videos.translations.*',
  ];

  const { data: beefProduct, error, isLoading: beefProductLoading } = useSWR({
        url: `beef_product/${beefProductId}`,
        fields,
    }, fetcher);
  
  const recipeFields = 'id,total_time,ingredients_list,layout_translations.*,imagegallery.*,difficulty,yields,cooking_method.cooking_method_id.translations.*';

  // fetch recipes
  const recipeFilters = {
    '_and': ([
      {
        '_or': [
          {
            retailers: {
              retailer_id: {
                '_eq': retailer.id
              }
            }
          },
          {
            retailer: {
              id: {
                '_eq': retailer.id
              }
            }
          }
        ]
      },
      {
        status: {
          '_eq': 'published'
        }
      },
      ...[
        !!beefProductId ?
          {
            beef_products: {
              beef_product_id: {
                '_eq': beefProductId
              }
            },
          } : {}
      ],
    ])
  };

  const {
    data: recipes,
    isLoading: recipesLoading
  } = useSWR({ url: `recipe`, fields: recipeFields, filter: recipeFilters }, fetcher);
  
  useUpdateDocumentMeta(beefProduct?.name);

  let foodServiceData = {};

  let migratedPreparationVideoIds = [];
  let migratedCulinaryVideoIds = [];
  let migratedMerchandisingVideoIds = [];
  let relatedCuts = [];
  let childCuts = [];

  if (!!beefProduct) {
    foodServiceData = getTranslation(
      beefProduct,
      {
        key: 'languages_code',
        code: language.code
      },
      'food_service_configurations',
    );

    migratedPreparationVideoIds = extractOrderedCmsVideoIdsFromLayoutVideos(
      foodServiceData?.prep_vids || [],
    );
    migratedCulinaryVideoIds = extractOrderedCmsVideoIdsFromLayoutVideos(
      foodServiceData?.culinary_vids || [],
    );
    migratedMerchandisingVideoIds = extractOrderedCmsVideoIdsFromLayoutVideos(
      foodServiceData?.merchandising_vids || [],
    );

    relatedCuts = beefProduct?.food_service_related_cuts?.map(({ related_beef_product_id: beefCut }) => {
      if (!beefCut) {
        return null;
      } else {
        const beefCutOverride = getBeefProductOverride(beefCut, retailer, language);
        const translatedBeefCutOverride = getTranslation(
          beefCutOverride,
          {
            key: 'languages_code',
            code: language.code
          },
        );

        const translatedBeefCut = getTranslation(
          beefCut,
          {
            key: 'languages_id',
            code: language.code
          }
        );

        const foodServiceConfigurations = getTranslation(
          beefCut,
          {
            key: 'languages_code',
            code: language.code
          },
          'food_service_configurations'
        );

        const specifications =
          foodServiceConfigurations?.specifications ||
          translatedBeefCutOverride?.specifications ||
          translatedBeefCut?.specifications;

        const image = beefCut?.Alternate_Food_Service_Image || beefCut?.image;

        return {
          id: beefCut.id,
          url: `/recipe-list`,
          search: `?beef_cut=${beefCut.id}`,
          title: translatedBeefCutOverride.name || translatedBeefCut.name,
          subtext: specifications,
          imageUrl: image ? `${process.env.REACT_APP_API_URL}/assets/${image}?key=tile-background-image-beef-wholesale` : null,
        }
      }
    });

    childCuts = beefProduct?.children?.map(beefCut => {
      if (!beefCut) {
        return null;
      } else {
        const beefCutOverride = getBeefProductOverride(beefCut, retailer, language);
        const translatedBeefCutOverride = getTranslation(
          beefCutOverride,
          {
            key: 'languages_code',
            code: language.code
          },
        );

        const translatedBeefCut = getTranslation(
          beefCut,
          {
            key: 'languages_id',
            code: language.code
          }
        );

        const foodServiceConfigurations = getTranslation(
          beefCut,
          {
            key: 'languages_code',
            code: language.code
          },
          'food_service_configurations'
        );

        const specifications =
          foodServiceConfigurations?.specifications ||
          translatedBeefCutOverride?.specifications ||
          translatedBeefCut?.specifications;
        
        const image = beefCut?.Alternate_Food_Service_Image || beefCut?.image;

        return {
          id: beefCut.id,
          url: `/recipe-list`,
          search: `?beef_cut=${beefCut.id}`,
          title: translatedBeefCutOverride.name || translatedBeefCut.name,
          subtext: specifications,
          imageUrl: image ? `${process.env.REACT_APP_API_URL}/assets/${image}?key=tile-background-image-beef-wholesale` : null,
        }
      }
    });

    const beefOverride = getBeefProductOverride(beefProduct, retailer, language);
    const translatedBeefOverride = getTranslation(
      beefOverride,
      {
        key: 'languages_code',
        code: language.code
      },
    );

    const translatedBeef = getTranslation(
      beefProduct,
      {
        key: 'languages_id',
        code: language.code
      }
    );

    // handle beef name & description overrides
    beefProduct.name = translatedBeefOverride.name || translatedBeef.name;
    beefProduct.description = translatedBeefOverride.description || translatedBeef.description;

    // handle beef image & icon overrides
    // TODO clean this up
    beefProduct.image = translatedBeefOverride.image || beefProduct.image;
    beefProduct.svg_icon = translatedBeefOverride.svg_icon || beefProduct.svg_icon;

    // specifications foldable
    beefProduct.specifications = translatedBeefOverride.specifications || translatedBeef.specifications;

    // handle beef name & description overrides
    beefProduct.name = getTranslation(
      getBeefProductOverride(beefProduct, retailer, language),
      {
        key: 'languages_code',
        code: language.code
      }).name ||
      getTranslation(
        beefProduct,
        {
          key: 'languages_id',
          code: language.code
        }).name;
    
    // append catalogue code
    const catalogCodeText = beefProduct.catalog_code ? ` (${beefProduct.catalog_code})` : '';
    beefProduct.name = `${beefProduct.name} ${catalogCodeText}`;

    const specifications = foodServiceData.specifications || translatedBeefOverride.specifications || translatedBeef.specifications;
    
    const description = translatedBeefOverride.description || translatedBeef.description;
    
    beefProduct.description = specifications || description;

    // handle beef image & icon overrides
    beefProduct.image = getBeefProductOverride(beefProduct, retailer, language).image || beefProduct.image;
    beefProduct.svg_icon = getBeefProductOverride(beefProduct, retailer, language).svg_icon || beefProduct.svg_icon;

    // specifications foldable
    beefProduct.specifications = specifications;
  }

  const handleSearchTriggered = () => {
    // hide recipe list
    setIsSearchActive(true);
  };

  const handleSearchCleared = async () => {
    setIsLoading(false);
    // show recipe list
    setIsSearchActive(false);
  };

  if (isLoading || beefProductLoading) {
    return <Loader />
  }
  
  return (
    <Box
      className="beef-product"
    > 
      <Container maxWidth disableGutters
        sx={{
          p: 0
        }}
      >
        <Box>
          <PageHeader
            item={{ 
              name: beefProduct?.name,
            }}
            useAsLink={false}
          />
        </Box>
      </Container>

      <Container
        fixed
        className='padding-on-mobile'
        sx={{
            mt: 5,
            mb: 3,
        }}
      >
        {
          !!beefProduct?.Alternate_Food_Service_Image &&
          <Stack
            alignItems={'center'}
            justifyContent={'center'}
          >
            <Grid
              className='accordion_section_img_container'
              item
              sm={12}
              sx={{
                mb: 3,
                textAlign: 'center'
              }}
            >
              <img
                className="accordion_section_img"
                src={`${process.env.REACT_APP_API_URL}/assets/${beefProduct.Alternate_Food_Service_Image}?key=beef-cut-image`}
                alt={getTranslation(beefProduct).name}
              />
            </Grid>
          </Stack>
        }

        {
          !!!beefProduct?.Alternate_Food_Service_Image && !!beefProduct?.image &&
          <Stack
            alignItems={'center'}
            justifyContent={'center'}
          >
            <Grid
              className='accordion_section_img_container'
              item
              sm={12}
              sx={{
                mb: 3,
                textAlign: 'center'
              }}
            >
              <img
                className="accordion_section_img"
                src={`${process.env.REACT_APP_API_URL}/assets/${beefProduct.image}?key=beef-cut-image`}
                alt={getTranslation(beefProduct).name}
              />
            </Grid>
          </Stack>
        }

        <GordonCard
          title={t('gordonConnectTitle')}
          content={t('gordonConnectContent')}
          url="https://gfs.ca/en-ca/products-services/technology-solutions/gordon-ordering/"
          externalLink={true}
        />
      </Container>

      <Container
        fixed
        className='padding-on-mobile'
        sx={{
          px: {
            xs: 0,
            md: '1.5rem'
          }
        }}
      >

        <BeefCuts
          beefCuts={beefProduct?.children || []}
        />

        {
          (!!beefProduct?.specifications) &&
          <AccordionSection
            title={t('pointsRequiringSpecification')}
            expanded={true}
          >
            <Box
              sx={{
                my: 3,
              }}
            >
              <Typography
                className='search-message'
                sx={{
                  color: '#000',
                  fontWeight: 300,
                  fontSize: '18px',
                  textAlign: 'justify'
                }}
                component="div"
              >
                <Wysiwyg>
                  {beefProduct?.specifications}
                </Wysiwyg>
              </Typography>
            </Box>
          </AccordionSection>
        }

        {
          (!!beefProduct?.food_service_related_cuts?.length) &&
          <AccordionSection
            title={t('relatedCuts')}
            expanded={true}
            accordionDetailsProps={{
              sx: {
                px: {
                  xs: 0,
                  md: 2,
                },
              }
            }}
          >
            <Box
              sx={{
                my: 3
              }}
            >
              <Grid container>
                {
                  relatedCuts
                  ?.sort((a, b) => {
                    const nameA = a.title;
                    const nameB = b.title;

                    if (nameA < nameB) {
                      return -1;
                    }
                    if (nameA > nameB) {
                      return 1;
                    }

                    // names must be equal
                    return 0;
                  })
                  ?.map(relatedCut => (
                    <Grid item
                      xs={12}
                      md={6}
                    >
                      <TileCard
                        key={relatedCut.id}
                        url={relatedCut.url}
                        search={relatedCut.search}
                        title={relatedCut.title}
                        subtext={relatedCut.subtext}
                        imageUrl={relatedCut.imageUrl}
                      />
                    </Grid>
                  ))
                }
              </Grid>
            </Box>
          </AccordionSection>
        }

        {
          !!migratedPreparationVideoIds.length &&
          <AccordionSection
            title={t('preparation')}
            expanded={true}
          >
            <Box
              sx={{
                my: 3,
              }}
            >
              <Box
                sx={{
                  textAlign: 'center',
                }}
              >
                <VideoGallery videos={migratedPreparationVideoIds} mode="light" />
              </Box>
            </Box>
          </AccordionSection>
        }

        {
          !!migratedCulinaryVideoIds.length &&
          <AccordionSection
            title={t('culinary')}
            expanded={true}
          >
            <Box
              sx={{
                my: 3,
              }}
            >
              <Box
                sx={{
                  textAlign: 'center',
                }}
              >
                <VideoGallery videos={migratedCulinaryVideoIds} mode="light" />
              </Box>
            </Box>
          </AccordionSection>
        }

        {
          (!!migratedMerchandisingVideoIds.length || !!childCuts?.length) &&
          <AccordionSection
            title={t('merchandising')}
            expanded={true}
          >
            <Box
              sx={{
                my: 3
              }}
            >
              {!!migratedMerchandisingVideoIds.length && (
                <Box
                  sx={{
                    textAlign: 'center',
                    mb: 2,
                  }}
                >
                  <VideoGallery videos={migratedMerchandisingVideoIds} mode="light" />
                </Box>
              )}

              <Grid container>
                {
                  childCuts
                  ?.sort((a, b) => {
                    const nameA = a.title;
                    const nameB = b.title;

                    if (nameA < nameB) {
                      return -1;
                    }
                    if (nameA > nameB) {
                      return 1;
                    }

                    // names must be equal
                    return 0;
                  })
                  ?.map(relatedCut => (
                    <Grid item
                      xs={12}
                      md={6}
                    >
                      <TileCard
                        key={relatedCut.id}
                        url={relatedCut.url}
                        search={relatedCut.search}
                        title={relatedCut.title}
                        subtext={relatedCut.subtext}
                        imageUrl={relatedCut.imageUrl}
                      />
                    </Grid>
                  ))
                }
              </Grid>
            </Box>
          </AccordionSection>
        }

        {
          (beefProduct?.threed_file || beefProduct?.threed_file_annotated) &&
          <AccordionSection
            title={t('3D')}
            expanded={true}
          >
            <Grid
              container
              spacing={2}
              sx={{
                mb: 2
              }}
            >
              <Grid item xs={12}>
                <ModelViewer3D
                  file={beefProduct?.threed_file}
                  fileAnnotated={beefProduct?.threed_file_annotated}
                />
              </Grid>
            </Grid>
          </AccordionSection>
        }

        {
          recipes?.length > 0 &&
          <AccordionSection
            title={t('recipes')}
            expanded={true}
            accordionDetailsProps={{
              sx: {
                px: {
                  xs: 0,
                  md: 2,
                },
              }
            }}
          >
            <Box
              sx={{
                my: 3
              }}
            >
              <Container
                className={'padding-on-mobile'}
                sx={{
                  py: 2
                }}
              >
                <Search
                  handleSearchTriggered={handleSearchTriggered}
                  handleSearchCleared={handleSearchCleared}
                  handleResultsFound={handleSearchTriggered}
                  storageKey='recipe-search'
                />
              </Container>

              {
                !isSearchActive &&
                <TileGrid
                  beefProductId={beefProductId}
                  tiles={recipes}
                />
              }
            </Box>
          </AccordionSection>
        }

        {
          beefProduct?.collections?.map(collection => (
            <Box
              key={collection.id}
            >
              <AccordionSection
                title={getTranslation(collection, { key: 'languages_code', code: language.code}).title}
                expanded={true}
              >
                <Collection
                  id={collection.id}
                  content
                  image
                />
              </AccordionSection>
            </Box>
          ))
        }
      </Container>
    </Box>
  )
}

export default BeefPrimal